import { useCallback, useMemo, useState } from 'react';
import framework from 'utils/framework';
import LanguageLocaleUtils from 'utils/LanguageLocale';
import SignInEN from 'pages/Auth/locales/SignIn_en_US';
import SignInFR from 'pages/Auth/locales/SignIn_fr_CA';
import {
    colors, fontSizes, fontWeights, forms, lineHeights, space
} from 'style/config';
import Button from 'components/Button/Button.f';
import Divider from 'components/Divider/Divider.f';
import Link from 'components/Link/Link.f';
import Radio from 'components/Inputs/Radio/Radio';
import PasswordRevealInput from 'components/Inputs/PasswordRevealInput';
import Icon from 'components/Icon/Icon.f';
import FormValidator from 'utils/FormValidator';
// ACS-4416 (VQA): masked-phone sub-line under the SMS OTP option reuses the shared
// SIM-4 / ACS-4128 masking utility (maskPhone -> `(***) ***-1234`, BR-019/BR-021).
import { maskPhone } from 'utils/credentialMasking';
// SIM-8 / ACS-4132 — inline password submit + risk-based step-up routing.
import authenticationApi from 'services/api/authentication';
import urlUtils from 'utils/Url';
import { MFA_MODE } from 'constants/phoneVerification';
import useSubmitGuard from 'pages/Auth/SignIn/hooks/useSubmitGuard';
// SIM-9 / ACS-4133 — SMS/Terms/Privacy legal anchors sourced from the shared
// constants (single source of truth across the auth screens).
import { TEXT_TERM_LINK, PRIVACY_POLICY_LINK, TERMS_OF_USE_LINK } from 'constants/legal';

const { wrapFunctionalComponent } = framework;
const getText = LanguageLocaleUtils.getLocalisedDataFromResource(SignInEN, SignInFR);

const noop = () => {};

// Available-auth-method identifiers from the OI-4 authenticationMethods lookup
// (api-contracts §1). Compared case-insensitively so an UPPERCASE producer
// (CredentialEntryScreen emits 'EMAIL'/'PHONE') never breaks matching (F-001).
const METHOD_OTP_SMS = 'OTP_SMS';
const METHOD_OTP_EMAIL = 'OTP_EMAIL';
const METHOD_PASSWORD = 'USERNAME_PASSWORD';

// mfaMode values committed at the SELECT seam. 'phone'/'email' seed the OTP
// session + advance to entry (in SignIn.f.jsx's handleSelectMethod); 'password'
// is now handled INLINE on this screen (submits via authenticationApi.login,
// then either routes to 'risk-step-up' or hands off to completeAuthentication).
const MODE_PHONE = 'phone';
const MODE_EMAIL = 'email';
const MODE_PASSWORD = 'password';

const hasMethod = (methods, target) => Array.isArray(methods) && methods.some(m => String(m).toUpperCase() === target);

// The Edit affordance is a link (not a button) so its "email"-bearing aria-label
// never collides with a method OPTION in role/name queries; the click is
// intercepted (preventDefault) and routed via goTo — the href is only a graceful
// no-JS fallback back to the sign-in entry point.
const SIGNIN_ROUTE = '/signin';
const RESET_PASSWORD_ROUTE = '/reset-password';

// Wrong-password mapping (migrated from PasswordFallbackScreen — behavior preserved).
// The backend surfaces ERR_INVALID_CREDENTIALS on 401 for this flow; a bare 401 (no
// enumerable errorCode) is also treated as wrong password per the plan's Stage-8
// fallback (MISSING_IN_ARCH). Everything else — 5xx / network / opaque failure —
// routes to AC-034 generic (BR-011: NO lockout, unlimited retries).
const WRONG_PASSWORD_ERROR_CODES = new Set(['ERR_INVALID_CREDENTIALS', 'ERR_WRONG_PASSWORD']);
const HTTP_UNAUTHORIZED = 401;
const isWrongPassword = err => WRONG_PASSWORD_ERROR_CODES.has(err?.errorCode) || err?.responseStatus === HTTP_UNAUTHORIZED;

// Display the credential in the header row. Normally we show the RAW value the
// customer typed: phone gets NANP formatting ((xxx) xxx-xxxx), email is shown
// verbatim (Figma 270-84177 shows the entered value, not the masked destination).
//
// AC-AUTH-REFRESH (SIM-7 / ACS-4131): after a page refresh on the authorization
// step the raw enteredCredential is intentionally NOT restored — it is PII and is
// never persisted to sessionStorage (BR-021). In that case we fall back to the
// non-PII MASKED forms that ARE persisted: maskedEmail for email, and the masked
// phone `(***) ***-{phoneLast4Digits}` for phone. The masked fallback keeps the
// header meaningful across a refresh without ever storing PII.
const credentialDisplay = ({ enteredCredential, credentialType, maskedEmail, phoneLast4Digits }) => {
    const isPhone = String(credentialType).toUpperCase() === 'PHONE';

    if (enteredCredential) {
        if (isPhone) {
            return FormValidator.getFormattedPhoneNumber(String(enteredCredential)) || enteredCredential;
        }

        return enteredCredential;
    }

    // Refresh-restore: no raw credential available -> masked, non-PII fallback.
    if (isPhone) {
        return phoneLast4Digits ? `(***) ***-${phoneLast4Digits}` : '';
    }

    return maskedEmail || '';
};

/**
 * AuthorizationScreen (SIM-7 / ACS-4131, Phase 2 — Figma nodes 270-84177 + 270-87274).
 *
 * OTP-first authorization for a single resolved account, presented as a RADIO
 * group with the option-specific content revealed INLINE beneath the selected
 * radio. Options render in the fixed priority order SMS -> Email -> Password
 * (BR-007 / AC-005); each is gated by eligibility (BR-008): SMS only when
 * OTP_SMS ∈ methods AND a phone last-4 exists, Email when OTP_EMAIL, Password
 * when USERNAME_PASSWORD.
 *
 * OTP modes (SMS/Email — Figma 270-84177): the primary button reads "Send" and
 * simply calls onSelectMethod(selectedMode) — the actual OTP send lives in
 * OtpEntryScreen. The SMS-consent copy (with functional Text Terms + Privacy
 * Policy links, BR-033) renders ONLY beneath the SMS option.
 *
 * Password mode (Figma 270-87274 — SIM-8 / ACS-4132): reveals INLINE a password
 * field (PasswordRevealInput — eye/show-hide toggle + a11y), a right-aligned
 * "Forgot Password?" link (Url.redirectTo('/reset-password')), Terms/Privacy
 * legal copy, and the primary button relabels to "Sign In". Submit calls
 * authenticationApi.login({ login: credential, password }) via useSubmitGuard;
 * wrong password / 401 -> wrongPasswordError copy (NO lockout, BR-011); 5xx ->
 * genericError (AC-034); mfaEnabled -> derive channel from shouldTriggerPhoneMFA
 * (mirror UserActions.js:1642/1661; do NOT read response.mfaMode) and
 * goTo('risk-step-up', {mfaMode, phoneLast4Digits?, credential}); success ->
 * completeAuthentication({method:'password', ...response}).
 *
 * An "Edit" affordance returns to credential entry (goTo('credential')) WITHOUT
 * issuing a new lookup or OTP send and WITHOUT touching the OTP session counters
 * (BR-041 / BR-025) — it never calls otpSession. No "passcode" terminology
 * anywhere; "verification code" is used (BR-034).
 *
 * @param {object} props
 * @param {(screen: string, contextPatch?: object) => void} [props.goTo]
 * @param {string[]} props.methods - availableAuthMethods from the lookup.
 * @param {string} [props.phoneLast4Digits]
 * @param {string} [props.maskedEmail] - masked email fallback used for the header
 *   display after a refresh-restore, when the raw enteredCredential (PII) is absent.
 * @param {string} [props.enteredCredential]
 * @param {'EMAIL'|'PHONE'} [props.credentialType]
 * @param {(mfaMode: 'phone'|'email') => void} [props.onSelectMethod] - invoked on
 *   Send for OTP modes only. The password branch submits inline on this screen.
 * @param {string} [props.credential] - raw credential (= enteredCredential) used
 *   for the inline password login call and threaded through to the step-up context.
 * @param {Function} [props.completeAuthentication] - SIM-10 completion handler. For the
 *   password-success (no step-up) branch it is called with the already-obtained login
 *   `response` so it completes the session from it (no second login) + redirects.
 */
const AuthorizationScreen = ({
    goTo = noop,
    methods,
    phoneLast4Digits,
    maskedEmail,
    enteredCredential,
    credentialType,
    onSelectMethod = noop,
    credential,
    completeAuthentication = noop
}) => {
    // Eligible options in the fixed OTP-first order (BR-007 / AC-005). Building the
    // list once keeps the default-selection and render loop in lock-step.
    const options = useMemo(() => {
        const list = [];

        if (hasMethod(methods, METHOD_OTP_SMS) && Boolean(phoneLast4Digits)) {
            list.push({ mode: MODE_PHONE, label: getText('smsMethodLabel'), hasConsent: true });
        }

        if (hasMethod(methods, METHOD_OTP_EMAIL)) {
            list.push({ mode: MODE_EMAIL, label: getText('emailMethodLabel'), hasConsent: false });
        }

        if (hasMethod(methods, METHOD_PASSWORD)) {
            list.push({ mode: MODE_PASSWORD, label: getText('passwordMethodLabel'), hasConsent: false });
        }

        return list;
    }, [methods, phoneLast4Digits]);

    // Default selection = FIRST eligible option (Figma: SMS if eligible, else
    // Email, else Password). Local state; only "Send"/"Sign In" commit it.
    const [selectedMode, setSelectedMode] = useState(() => options[0]?.mode);
    const [password, setPassword] = useState('');
    const [passwordError, setPasswordError] = useState(null);

    const isPasswordMode = selectedMode === MODE_PASSWORD;

    // Inline password-submit action + guard (BR-024/AC-025 double-submit + 200ms
    // delayed loader + 10s timeout). Rebuilt when credential/password change so
    // useSubmitGuard closes over the latest values (matches PasswordFallbackScreen).
    const submitAction = useCallback(
        async () =>
            authenticationApi.login({
                login: credential,
                password,
                options: { isKeepSignedIn: false }
            }),
        [credential, password]
    );

    const { submit, inFlight, showTimeout } = useSubmitGuard(submitAction);

    const goToCredential = () => goTo('credential');

    const handleSelectMode = mode => {
        setSelectedMode(mode);
        // Clear stale password error state when the customer switches away from
        // (or back to) the password option; the field itself keeps its value so
        // a mis-toggle doesn't force retyping.
        setPasswordError(null);
    };

    const handlePasswordChange = event => {
        setPassword(event?.target?.value ?? '');
    };

    const handleForgotPassword = event => {
        // Prevent Link's default anchor-like behaviour and route via the SPA-safe
        // Url.redirectTo pattern (matches useAuthFlow.js:64 / useReturnUrl.js).
        event?.preventDefault?.();
        urlUtils.redirectTo(RESET_PASSWORD_ROUTE);
    };

    const handlePasswordSubmit = async event => {
        event?.preventDefault?.();
        setPasswordError(null);

        // Refresh-restore recovery: the raw credential is PII and is intentionally
        // never persisted (BR-021), so after a refresh on the authorization step it
        // is absent. Submitting would call authenticationApi.login({ login: undefined })
        // and always fail — instead route back to credential entry so the customer
        // re-enters it cleanly.
        if (!credential) {
            goToCredential();

            return;
        }

        // Empty-password guard: a blank field must NOT hit authenticationApi.login
        // (which would surface the wrong-password copy for a missing input). Show the
        // required-field message inline and re-enable the field.
        if (!password) {
            setPasswordError(getText('passwordRequired'));

            return;
        }

        let response;

        try {
            response = await submit();
        } catch (err) {
            // BR-011: NO lockout — every wrong-password attempt shows the same
            // AC-020 copy and re-enables the field. Any other failure surfaces
            // the AC-034 generic copy (5xx / network / opaque).
            setPasswordError(isWrongPassword(err) ? getText('wrongPasswordError') : getText('genericError'));

            return;
        }

        // useSubmitGuard swallows a duplicate in-flight submit → undefined.
        if (response == null) {
            return;
        }

        if (response.mfaEnabled) {
            // BR-040 forced-challenge: derive channel from the SAME two shouldTrigger*
            // flags UserActions.js:1642/1661 branches on. The resolved value has NO
            // mfaMode field — reading response.mfaMode would silently drop the branch.
            const stepUpChannel = response.shouldTriggerPhoneMFA ? MFA_MODE.PHONE : MFA_MODE.EMAIL;
            // Masked destination for the step-up OTP copy ("code sent to ***"). Mirrors
            // maskedDestinationFor() on the OTP-first path (SignIn.f.jsx): phone uses the
            // login response's phoneLast4Digits, email uses the maskedEmail prop. Without
            // this in the patch, useOtpSession.sendInitial seeds a BLANK maskedDestination
            // and RiskStepUpOtpScreen renders an empty destination.
            const maskedDestination = stepUpChannel === MFA_MODE.PHONE ? `(***) ***-${response.phoneLast4Digits}` : maskedEmail;
            goTo('risk-step-up', {
                mfaMode: stepUpChannel,
                // Only carry phoneLast4Digits when the phone branch actually provides
                // it — the email branch's payload doesn't include one.
                ...(response.phoneLast4Digits && { phoneLast4Digits: response.phoneLast4Digits }),
                maskedDestination,
                credential
            });

            return;
        }

        // Success path (no step-up): the inline login() above already performed the
        // ONE terminal login (it minted tokens — login.js:240-243). Hand that
        // already-obtained, token-shaped `response` to completeAuthentication, which
        // completes the session from it via signInSuccessAfterMFA (profile status +
        // Redux + analytics) and then redirects to the returnUrl — WITHOUT logging in
        // a second time. Passing `response` (NOT spreading it) is what prevents the
        // double login: a spread response would send email/password as undefined into
        // the thunk's legacy signIn() → authenticationApi.login() path.
        // Wrapped in try/catch (mirrors OtpEntryScreen.f.jsx) so a completion/redirect
        // failure surfaces the AC-034 generic copy inline, not an unhandled rejection.
        try {
            await completeAuthentication({
                method: 'password',
                response,
                email: credential,
                keepSignedIn: false
            });
        } catch (err) {
            setPasswordError(getText('genericError'));
        }
    };

    // The primary button behaves differently per selected mode:
    //   - OTP modes: click -> onSelectMethod(selectedMode); label "Send"
    //   - Password: click -> handlePasswordSubmit; label "Sign In"; loading state
    const handlePrimaryClick = () => {
        if (isPasswordMode) {
            // handlePasswordSubmit handles all of its own errors internally (submit
            // + completeAuthentication are both try/caught), so the promise never
            // rejects; swallow defensively so no floating promise can leak.
            Promise.resolve(handlePasswordSubmit()).catch(noop);

            return;
        }

        if (selectedMode) {
            onSelectMethod(selectedMode);
        }
    };

    // Reveal the timeout copy alongside a fresh wrong-password error surface:
    // both are inline-alert regions; the timeout carries BR-026/AC-026 copy.
    const timeoutMessage = isPasswordMode && showTimeout ? getText('timeoutMessage') : null;

    // Enter submits the screen (same as clicking the primary button): "Send" for
    // OTP modes, "Sign In" for password. The screen is a <div> (not a <form>), so
    // there is no native submit-on-Enter — this keydown supplies it. Guards:
    //   - only Enter (ignore Shift+Enter and every other key);
    //   - ignore while a password submit is in flight (matches the button's
    //     disabled/loading state, preventing a double-submit);
    //   - ignore Enter that originates on an interactive child (the Edit / Forgot
    //     Password links and the primary button already handle Enter natively), so
    //     we don't fire the primary action twice.
    const handleRootKeyDown = event => {
        if (event.key !== 'Enter' || event.shiftKey) {
            return;
        }

        if (isPasswordMode && inFlight) {
            return;
        }

        const target = event.target;
        const tag = target?.tagName;

        if (tag === 'A' || tag === 'BUTTON' || target?.closest?.('a, button')) {
            return;
        }

        event.preventDefault();
        handlePrimaryClick();
    };

    return (
        <div
            css={styles.root}
            onKeyDown={handleRootKeyDown}
        >
            {/* Header block (Figma 270-84177): the entered credential + an Edit link
                side by side. The single "Sign In" page <h1> (SignIn.f.jsx) is the
                heading — this screen intentionally renders NO heading of its own to
                avoid a duplicate "Sign In" (the radiogroup's aria-label is this
                screen's semantic label). No back chevron in this frame — Edit is the
                return-to-credential affordance and it does not re-lookup/send
                (AC-AUTH-EDIT / AC-AUTH-BACK). */}
            <div css={styles.header}>
                <div css={styles.credentialRow}>
                    <span css={styles.credential}>{credentialDisplay({ enteredCredential, credentialType, maskedEmail, phoneLast4Digits })}</span>
                    {/* Design-system Link (renders a button with a span aria role, so
                        its aria-label — which necessarily contains "email" — does not
                        collide with a method OPTION when queries filter by /email/i).
                        href is a graceful no-JS fallback; the click is intercepted and
                        routed via goTo. */}
                    <Link
                        href={SIGNIN_ROUTE}
                        color='link'
                        underline={true}
                        aria-label={getText('editAriaLabel')}
                        onClick={event => {
                            event.preventDefault();
                            goToCredential();
                        }}
                    >
                        {getText('editLabel')}
                    </Link>
                </div>
            </div>

            <Divider css={styles.divider} />

            {/* Radio option group in fixed OTP-first order (BR-007 / AC-005). Reuses
                the design-system Radio (components/Inputs/Radio) — it owns the dot,
                the hidden role="radio" input, label association, and focus ring. */}
            <div
                role='radiogroup'
                aria-label={getText('authorizationSubtitle')}
                css={styles.radioGroup}
            >
                {options.map(option => (
                    <div key={option.mode}>
                        <Radio
                            id={`authMethod-${option.mode}`}
                            name='authMethod'
                            value={option.mode}
                            checked={option.mode === selectedMode}
                            onChange={() => handleSelectMode(option.mode)}
                            alignItems='flex-start'
                            paddingY={null}
                        >
                            {option.label}
                            {/* ACS-4416 (VQA): masked mobile number so the customer sees
                                which number the code goes to. Rendered INSIDE the Radio
                                children so it is part of the radio's associated <label>
                                and therefore its accessible name (screen readers announce
                                the destination, not just "Text me a verification code").
                                Reuses the SIM-4/ACS-4128 maskPhone util; phoneLast4Digits
                                is guaranteed present here (the SMS option is gated on it). */}
                            {option.mode === MODE_PHONE && Boolean(phoneLast4Digits) && (
                                <span css={styles.maskedPhone}>{maskPhone(phoneLast4Digits)}</span>
                            )}
                        </Radio>
                        {/* SMS-consent copy directly below the SMS option only
                            (BR-033 / AC-AUTH-SMSCONSENT); rendered OUTSIDE the Radio
                            label so it is not part of the clickable control. Hidden
                            when the password mode is active (Figma 270-87274 replaces
                            this block with the password field). */}
                        {option.hasConsent && !isPasswordMode && (
                            <p css={styles.consent}>
                                {getText('smsConsentPrefix')}
                                <Link
                                    href={urlUtils.getLink(TEXT_TERM_LINK)}
                                    color='link'
                                    underline={true}
                                    css={styles.consentLink}
                                >
                                    {getText('smsConsentTextTerms')}
                                </Link>
                                {getText('smsConsentMiddle')}
                                <Link
                                    href={urlUtils.getLink(PRIVACY_POLICY_LINK)}
                                    color='link'
                                    underline={true}
                                    css={styles.consentLink}
                                >
                                    {getText('smsConsentPrivacy')}
                                </Link>
                                {getText('smsConsentSuffix')}
                            </p>
                        )}
                    </div>
                ))}
            </div>

            {/* Password-selected inline state (Figma 283-92324). The password field is
                a FULL-WIDTH sibling of the radio group — it renders directly beneath
                all radio options at the root column indent (NOT indented under the
                "Enter password" radio), edge-to-edge, so its box aligns with the
                divider and the primary button. Contains: password field with the
                eye/show-hide toggle (PasswordRevealInput), an inline error alert, and
                a right-aligned "Forgot Password?" link (reuses the design-system Link,
                blue via color='link'). The 16px root gap separates it from the radios
                above and the legal copy / button below (Figma gap-[16px]). */}
            {isPasswordMode && (
                <div css={styles.passwordBlock}>
                    <PasswordRevealInput
                        id='authMethod-password-input'
                        name='password'
                        label={getText('passwordLabel')}
                        aria-label={getText('passwordLabel')}
                        autoComplete='current-password'
                        value={password}
                        onChange={handlePasswordChange}
                        marginBottom={0}
                    />
                    {passwordError && (
                        <div
                            role='alert'
                            aria-live='assertive'
                            css={styles.error}
                        >
                            <Icon
                                name='alert'
                                size={16}
                                css={styles.errorIcon}
                            />
                            <span>{passwordError}</span>
                        </div>
                    )}
                    {timeoutMessage && (
                        <div
                            role='alert'
                            aria-live='assertive'
                            css={styles.error}
                        >
                            <Icon
                                name='alert'
                                size={16}
                                css={styles.errorIcon}
                            />
                            <span>{timeoutMessage}</span>
                        </div>
                    )}
                    <div css={styles.forgotPasswordRow}>
                        {/* href gives true anchor semantics (open-in-new-tab / copy
                            link) + a graceful no-JS fallback to /reset-password; the
                            intercepted onClick (preventDefault + Url.redirectTo) keeps
                            SPA routing. Same pattern as the Edit link above. Without an
                            href, Link.f renders a <button> (uiUtils.getAriaButtonProps)
                            and loses those link affordances. */}
                        <Link
                            href={RESET_PASSWORD_ROUTE}
                            color='link'
                            underline={true}
                            onClick={handleForgotPassword}
                        >
                            {getText('forgotPassword')}
                        </Link>
                    </div>
                </div>
            )}

            {/* Password-mode legal copy above the "Sign In" button (Figma 283-92324):
                "By clicking 'Sign In', you (1) agree to the current version of our
                TERMS OF USE and (2) have read Sephora's PRIVACY POLICY." The Terms
                anchor points at TERMS_OF_USE_LINK (/beauty/terms-of-use) — NOT the
                SMS-specific TEXT_TERM_LINK used by the SMS-consent block; Privacy
                reuses the shared PRIVACY_POLICY_LINK. Both via urlUtils.getLink. */}
            {isPasswordMode && (
                <p css={styles.consentBelow}>
                    {getText('signInLegalPrefix')}
                    <Link
                        href={urlUtils.getLink(TERMS_OF_USE_LINK)}
                        color='link'
                        underline={true}
                        css={styles.consentLink}
                    >
                        {getText('termsOfUse')}
                    </Link>
                    {getText('signInLegalMiddle')}
                    <Link
                        href={urlUtils.getLink(PRIVACY_POLICY_LINK)}
                        color='link'
                        underline={true}
                        css={styles.consentLink}
                    >
                        {getText('smsConsentPrivacy')}
                    </Link>
                    {getText('signInLegalSuffix')}
                </p>
            )}

            <Button
                variant='primary'
                block={true}
                onClick={handlePrimaryClick}
                loading={isPasswordMode ? inFlight : undefined}
                disabled={isPasswordMode ? inFlight : undefined}
                css={styles.send}
            >
                {isPasswordMode ? getText('signInButton') : getText('sendButton')}
            </Button>
        </div>
    );
};

const styles = {
    // 400px column, 16px gap between blocks (Figma 270-84177).
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: space[4]
    },
    // Header block: 8px gap between the heading and the credential/Edit row.
    header: {
        display: 'flex',
        flexDirection: 'column',
        gap: space[2]
    },
    credentialRow: {
        display: 'flex',
        alignItems: 'center',
        gap: space[2]
    },
    credential: {
        color: colors.black,
        fontSize: fontSizes.base
    },
    // Full-width 1px #EEE rule (colors.lightGray).
    divider: {
        margin: 0,
        borderColor: colors.lightGray
    },
    radioGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: space[4]
    },
    // ACS-4416 (VQA): masked mobile number shown under the SMS option label. Rendered
    // INSIDE the Radio label (part of the accessible name), so it needs no left indent —
    // it already sits in the label column past the dot. `display: block` drops it onto
    // its own line beneath the label text; black BOLD 14px (base) so it reads as the
    // destination info (distinct from the grey fine-print consent below).
    maskedPhone: {
        display: 'block',
        color: colors.black,
        fontSize: fontSizes.base,
        fontWeight: fontWeights.bold,
        lineHeight: lineHeights.tight,
        marginTop: space[1]
    },
    // SMS-consent copy: 12px #666, 14px line-height; the two legal anchors are
    // UPPERCASE (via textTransform), bold, blue #136BEA, underlined.
    consent: {
        color: colors.gray,
        fontSize: fontSizes.sm,
        lineHeight: lineHeights.tight,
        marginTop: space[2],
        // Align under the Radio label (past the dot + its right margin).
        marginLeft: forms.RADIO_SIZE + forms.RADIO_MARGIN,
        marginBottom: 0
    },
    // Password-mode "By clicking Sign In..." legal copy above the primary button
    // (Figma 270-87274). Same typography as the SMS-consent block; sits at the
    // root indent (not under a radio) so no left indent.
    consentBelow: {
        color: colors.gray,
        fontSize: fontSizes.sm,
        lineHeight: lineHeights.tight,
        marginTop: 0,
        marginBottom: 0
    },
    // Bold + UPPERCASE deltas on top of the shared Link (which supplies the blue
    // colour + underline via its color/underline props).
    consentLink: {
        fontWeight: fontWeights.bold,
        textTransform: 'uppercase'
    },
    // Password-inline block (Figma 283-92324). FULL-WIDTH sibling of the radio
    // group: NO left indent — the field spans the column edge-to-edge, aligning
    // with the divider and primary button. The 16px root gap owns the separation
    // from the radios above / legal copy below, so no marginTop here. Internal
    // 8px gap stacks the field, inline error, and the right-aligned Forgot
    // Password link (Figma gap-[8px] within the input cluster).
    passwordBlock: {
        display: 'flex',
        flexDirection: 'column',
        gap: space[2]
    },
    // Same inline-alert recipe as CredentialEntryScreen (Figma "5. Errors" —
    // warning icon + error text in colors.error, 12px).
    error: {
        display: 'flex',
        gap: space[2],
        alignItems: 'flex-start',
        color: colors.error,
        fontSize: fontSizes.sm
    },
    errorIcon: {
        flexShrink: 0,
        color: colors.error
    },
    // Right-aligned "Forgot Password?" beneath the field (Figma 270-87274).
    forgotPasswordRow: {
        display: 'flex',
        justifyContent: 'flex-end'
    },
    send: {
        marginTop: 0
    }
};

export default wrapFunctionalComponent(AuthorizationScreen, 'AuthorizationScreen');
